import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { TableCard, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import { Wallet, HandCoins, Users, Clock } from "lucide-react";

function ageInfo(dateStr) {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days <= 30) return { days, label: "0-30d", cls: "bg-green-50 text-agri-forest-light" };
  if (days <= 60) return { days, label: "31-60d", cls: "bg-amber-50 text-agri-amber-dark" };
  if (days <= 90) return { days, label: "61-90d", cls: "bg-orange-100 text-orange-700" };
  return { days, label: "90+d", cls: "bg-red-50 text-agri-danger" };
}

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

export default function CreditLedger() {
  const { data } = useQuery({ queryKey: ["outstanding"], queryFn: () => api.get("/reports/outstanding").then((r) => r.data) });

  const clients = data?.clients || [];
  const advances = data?.advances || [];
  const oldestDays = clients.reduce((mx, c) => {
    if (!c.last_transaction) return mx;
    return Math.max(mx, Math.floor((Date.now() - new Date(c.last_transaction).getTime()) / 86400000));
  }, 0);

  return (
    <div className="space-y-6" data-testid="credit-ledger-page">
      <div>
        <div className="label-cap">Receivables</div>
        <h1 className="font-heading text-3xl mt-1">Credit Ledger</h1>
        <p className="text-sm text-agri-ink-2 mt-1">All clients with outstanding balances, sorted by amount.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Outstanding" value={fmtMoney(data?.total || 0)} icon={HandCoins} tone={(data?.total || 0) > 0 ? "danger" : "forest"} hint="Owed to you by customers" />
        <Stat label="Customers" value={clients.length} icon={Users} hint="with an outstanding balance" />
        <Stat label="Oldest Unpaid" value={oldestDays ? `${oldestDays}d` : "—"} icon={Clock} tone={oldestDays > 60 ? "danger" : oldestDays > 30 ? "amber" : "forest"} hint="since last transaction" />
        <Stat label="Store Credit Held" value={fmtMoney(data?.advances_total || 0)} icon={Wallet} hint={`${advances.length} customer${advances.length === 1 ? "" : "s"} · their money`} />
      </div>

      {advances.length > 0 && (
        <div className="card-soft p-4">
          <div className="label-cap mb-2">Customer advances / store credit</div>
          <div className="space-y-1.5">
            {advances.map((c) => (
              <div key={c.id} className="flex justify-between text-sm">
                <Link to={`/clients/${c.id}`} className="text-agri-forest hover:underline">{c.name}</Link>
                <span className="tabular-nums font-semibold text-agri-forest">{fmtMoney(-c.balance)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Client</th>
            <th className={thCls}>Phone</th>
            <th className={thCls}>Area</th>
            <th className={thCls}>Last Txn</th>
            <th className={thCls}>Age</th>
            <th className={thNumCls}>Balance</th>
            <th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {clients.length === 0 && <EmptyRow colSpan={7} icon={Wallet}>No outstanding balances.</EmptyRow>}
          {clients.map((c) => {
            const a = ageInfo(c.last_transaction);
            return (
              <tr key={c.id} className={rowCls}>
                <td className={`${tdCls} rt-primary`}><Link to={`/clients/${c.id}`} className="font-medium text-agri-forest hover:underline">{c.name}</Link></td>
                <td data-label="Phone" className={`${tdCls} text-agri-ink-2`}>{c.phone}</td>
                <td data-label="Area" className={tdCls}>{c.area}</td>
                <td data-label="Last Txn" className={`${tdCls} text-xs text-agri-ink-2`}>{c.last_transaction ? new Date(c.last_transaction).toLocaleDateString() : "—"}</td>
                <td data-label="Age" className={tdCls}>{a ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.cls}`}>{a.label}</span> : "—"}</td>
                <td data-label="Balance" className={`${tdNumCls} font-bold text-agri-danger`}>{fmtMoney(c.balance)}</td>
                <td className={`${tdCls} text-right rt-actions`}>
                  <Link to={`/clients/${c.id}`} title="Receive a payment"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-agri-forest/5 text-agri-forest">
                    <Wallet size={13} /> Receive
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableCard>
    </div>
  );
}
